import React, { useState, useEffect } from 'react';
import { Avatar, Box, Button, Grid, IconButton, Paper, Snackbar, Alert, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiFetch } from '../utils/apiFetch';

export default function MoverTeam({ token }) {
  const [team, setTeam] = useState([]);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await apiFetch('/api/team/me');
        setTeam(await res.json());
      } catch {
        setTeam([]);
      }
    };
    fetchTeam();
  }, []);

  const handleAdd = async () => {
    if (!name || !role) {
      setSnackbar({ open: true, message: 'Name and role are required', severity: 'error' });
      return;
    }
    
    try {
      const res = await apiFetch('/api/team/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, role, email, phone })
      });
      const data = await res.json();
      setTeam(data);
      setName('');
      setRole('');
      setEmail('');
      setPhone('');
      setSnackbar({ open: true, message: 'Team member added!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to add member.', severity: 'error' });
    }
  };

  const handleRemove = async idx => {
    try {
      const res = await apiFetch(`/api/team/me/${idx}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setTeam(await res.json());
      setSnackbar({ open: true, message: 'Team member removed!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove member.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F5F7FA', minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3, borderRadius: 3, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)', boxShadow: '0 12px 28px rgba(16,42,67,0.18)' }}>
        <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>OPERATIONS</Typography>
        <Typography variant="h4" sx={{ fontWeight: 850 }}>Team</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 0.5 }}>Keep crew contact details current for dispatch and job coordination.</Typography>
      </Paper>
      <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid #D9E2EC', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Add Team Member</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr auto' }, gap: 2, mb: 2 }}>
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" required />
          <TextField label="Role" value={role} onChange={e => setRole(e.target.value)} size="small" required />
          <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} size="small" type="email" />
          <TextField label="Phone" value={phone} onChange={e => setPhone(e.target.value)} size="small" />
          <Button variant="contained" onClick={handleAdd}>Add</Button>
        </Box>
        <Grid container spacing={2}>
          {team.length === 0 && <Grid item xs={12}><Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary', borderRadius: 2 }}>No team members yet.</Paper></Grid>}
          {team.map((member, idx) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#1E293B' }}>{member.name?.charAt(0) || 'M'}</Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={800} noWrap>{member.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{member.role}</Typography>
                  {(member.email || member.phone) && <Typography variant="caption" color="text.secondary" noWrap>{[member.email, member.phone].filter(Boolean).join(' • ')}</Typography>}
                </Box>
                <IconButton edge="end" color="error" onClick={() => handleRemove(idx)}><DeleteIcon /></IconButton>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
